const MUTE_KEY = "hackwerk-status-sound-muted";
const TOAST_VISIBLE_MS = 8000;

type WebkitWindow = Window & { webkitAudioContext?: typeof AudioContext };

export function isMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === "true";
  } catch {
    // Private mode / storage disabled: default to audible.
    return false;
  }
}

export function setMuted(muted: boolean): void {
  try {
    localStorage.setItem(MUTE_KEY, String(muted));
  } catch {
    // Nothing we can do; the in-page toggle still reflects the session.
  }
}

let audioContext: AudioContext | null = null;

/** Releases the cached AudioContext (teardown / tests); the next chime rebuilds it. */
export function resetStatusAudio(): void {
  audioContext = null;
}

function getAudioContext(): AudioContext | null {
  const Ctor = window.AudioContext || (window as WebkitWindow).webkitAudioContext;
  if (!Ctor) return null;
  // Reused across chimes on purpose - creating an AudioContext per notification
  // leaks hardware audio resources and browsers cap how many may exist.
  audioContext ??= new Ctor();
  return audioContext;
}

/**
 * Short two-tone chime, synthesised rather than loaded as a file: it keeps the strict
 * CSP (no extra media origin), adds no download, and matches the site's terminal feel.
 * Rising interval when the space opens, falling when it closes.
 */
export function playStatusChime(open: boolean): void {
  if (isMuted()) return;

  const ctx = getAudioContext();
  if (!ctx) return;

  // Browsers start the context suspended until a user gesture; resume is a no-op
  // (and safely rejects) when autoplay is still blocked.
  if (ctx.state === "suspended") void ctx.resume?.()?.catch?.(() => {});

  const now = ctx.currentTime;
  const tones = open ? [660, 990] : [520, 350];

  tones.forEach((frequency, index) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const start = now + index * 0.14;
    const end = start + 0.16;

    osc.type = "sine";
    osc.frequency.setValueAtTime(frequency, start);

    // Quick fade in/out so the tone doesn't click at the edges.
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.16, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);

    osc.connect(gain).connect(ctx.destination);
    osc.start(start);
    osc.stop(end + 0.02);
  });
}

function toastHost(): HTMLElement {
  let host = document.getElementById("statusToastHost");
  if (!host) {
    host = document.createElement("div");
    host.id = "statusToastHost";
    host.className = "status-toast-host";
    // Announced politely: a status flip is informative, not an interruption.
    host.setAttribute("role", "status");
    host.setAttribute("aria-live", "polite");
    document.body.append(host);
  }
  return host;
}

export function showStatusToast(open: boolean): HTMLElement {
  const host = toastHost();
  host.replaceChildren();

  const toast = document.createElement("div");
  toast.className = "status-toast";
  toast.dataset.state = open ? "open" : "closed";

  const dot = document.createElement("span");
  dot.className = "status-toast-dot";
  dot.setAttribute("aria-hidden", "true");

  const text = document.createElement("p");
  text.className = "status-toast-text";
  text.textContent = open ? "Der Space ist jetzt geöffnet." : "Der Space ist jetzt geschlossen.";

  const actions = document.createElement("div");
  actions.className = "status-toast-actions";

  const muteBtn = document.createElement("button");
  muteBtn.type = "button";
  muteBtn.className = "status-toast-btn";
  const syncMuteLabel = (): void => {
    const muted = isMuted();
    muteBtn.textContent = muted ? "Ton an" : "Ton aus";
    muteBtn.setAttribute("aria-label", muted ? "Benachrichtigungston einschalten" : "Benachrichtigungston ausschalten");
  };
  syncMuteLabel();
  muteBtn.addEventListener("click", () => {
    setMuted(!isMuted());
    syncMuteLabel();
  });

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "status-toast-btn";
  closeBtn.textContent = "Schließen";
  closeBtn.setAttribute("aria-label", "Mitteilung schließen");
  closeBtn.addEventListener("click", () => toast.remove());

  actions.append(muteBtn, closeBtn);
  toast.append(dot, text, actions);
  host.append(toast);

  // Next frame so the entry transition actually runs instead of being skipped.
  requestAnimationFrame(() => toast.classList.add("is-visible"));
  setTimeout(() => toast.remove(), TOAST_VISIBLE_MS);

  return toast;
}

/**
 * Announces a space-status flip. Returns false (and stays silent) on the very first
 * reading, so simply loading the page never fires a notification - only a genuine
 * change while the visitor is already on the site does.
 */
export function notifyStatusChange(previous: boolean | null, next: boolean): boolean {
  if (previous === null || previous === next) return false;

  showStatusToast(next);
  playStatusChime(next);
  return true;
}
